import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Link2, ExternalLink, FileText, Settings as SettingsIcon, ArrowUpRight } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SECTIONS = ['nav', 'footer', 'general'];
const TYPES = [
  { value: 'url', label: 'External URL', icon: ExternalLink },
  { value: 'route', label: 'Internal Route', icon: Link2 },
  { value: 'text', label: 'Text Value', icon: FileText }
];

const SAMPLE_SETTINGS = [
  { _id: '1', key: 'loginUrl', value: 'https://www.manager.avopla.ca/', type: 'url', section: 'nav', description: 'Login button URL' },
  { _id: '2', key: 'whyAvoplaRoute', value: '/', type: 'route', section: 'nav', description: 'Why Avopla route' },
  { _id: '3', key: 'pricingRoute', value: '/plans', type: 'route', section: 'nav', description: 'Pricing route' },
  { _id: '4', key: 'signInUrl', value: 'https://www.manager.avopla.ca/', type: 'url', section: 'footer', description: 'Sign In URL' },
  { _id: '5', key: 'privacyPolicyUrl', value: 'https://avopla.com/legal/privacy-policy', type: 'url', section: 'footer', description: 'Privacy Policy' },
  { _id: '6', key: 'copyrightYear', value: '2025', type: 'text', section: 'general', description: 'Copyright year' },
];

const Settings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    type: 'url',
    section: 'nav',
    description: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      setSettings(SAMPLE_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSetting) {
        await axios.put(`${API_URL}/settings/${editingSetting._id}`, formData);
        toast.success('Setting updated');
      } else {
        await axios.post(`${API_URL}/settings`, formData);
        toast.success('Setting created');
      }
      setShowModal(false);
      setEditingSetting(null);
      resetForm();
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      key: setting.key,
      value: setting.value,
      type: setting.type,
      section: setting.section,
      description: setting.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this setting?')) return;
    try {
      await axios.delete(`${API_URL}/settings/${id}`);
      toast.success('Deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ key: '', value: '', type: 'url', section: 'nav', description: '' });
  };

  const openNewModal = () => {
    setEditingSetting(null);
    resetForm();
    setShowModal(true);
  };

  const filteredSettings = settings.filter(s =>
    activeSection === 'all' || s.section === activeSection
  );

  const getTypeIcon = (type) => {
    const typeConfig = TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : FileText;
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Settings & Links</h1>
        <p>Manage URLs, routes, and configuration values</p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{filteredSettings.length}</strong> settings
        </span>
        <button className="btn btn-primary" onClick={openNewModal}>
          <Plus size={16} />
          Add Setting
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          onClick={() => setActiveSection('all')}
          className={`tab ${activeSection === 'all' ? 'active' : ''}`}
        >
          All Sections
        </button>
        {SECTIONS.map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`tab ${activeSection === section ? 'active' : ''}`}
            style={{ textTransform: 'capitalize' }}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Settings List */}
      {filteredSettings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Link2 size={28} />
          </div>
          <h3>No settings found</h3>
          <p>Add settings to configure URLs and links</p>
          <button className="btn btn-primary" onClick={openNewModal}>
            <Plus size={16} />
            Add Setting
          </button>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Type</th>
                  <th>Section</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSettings.map(setting => {
                  const TypeIcon = getTypeIcon(setting.type);
                  return (
                    <tr key={setting._id}>
                      <td>
                        <code>{setting.key}</code>
                      </td>
                      <td style={{ maxWidth: '200px' }}>
                        {setting.type === 'url' ? (
                          <a
                            href={setting.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            {setting.value.length > 35 ? setting.value.substring(0, 35) + '...' : setting.value}
                            <ArrowUpRight size={12} />
                          </a>
                        ) : (
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {setting.value}
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                          <TypeIcon size={14} />
                          {setting.type}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-primary" style={{ textTransform: 'capitalize' }}>
                          {setting.section}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                        {setting.description || '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline btn-icon btn-sm" onClick={() => handleEdit(setting)}>
                            <Edit2 size={14} />
                          </button>
                          <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(setting._id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSetting ? 'Edit Setting' : 'Add Setting'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Section</label>
                    <select
                      className="form-select"
                      value={formData.section}
                      onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                      required
                    >
                      {SECTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-select"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      required
                    >
                      {TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Key</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    placeholder="loginUrl, pricingRoute, etc."
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Value</label>
                  <input
                    type={formData.type === 'url' ? 'url' : 'text'}
                    className="form-input"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.type === 'url' ? 'https://example.com' : '/plans'}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Description <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this setting for?"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={16} />
                  {editingSetting ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
